import { useEffect, useRef } from 'react';

/**
 * 自定义hook，用于在浏览器标签页显示倒计时
 * @param {number} seconds - 倒计时秒数
 * @param {string} prefix - 标题前缀
 * @param {string} suffix - 标题后缀
 * @param {boolean} isActive - 是否激活倒计时
 * @param {string} originalTitle - 原始标题
 * @returns {Object} - 包含重置倒计时的函数
 */
const useTabCountdown = (seconds, prefix = '', suffix = '', isActive = true, originalTitle = 'PromptBattle') => {
  const originalTitleRef = useRef(document.title || originalTitle);
  const intervalRef = useRef(null);
  const endTimeRef = useRef(0);
  const secondsRef = useRef(seconds);
  
  // 更新引用值
  useEffect(() => {
    secondsRef.current = seconds;
  }, [seconds]);
  
  // 重置计时器的函数
  const resetTimer = (newSeconds = null) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (newSeconds !== null && newSeconds > 0 && isActive) {
      secondsRef.current = newSeconds;
      endTimeRef.current = Date.now() + newSeconds * 1000;
      
      // 立即更新标题
      const formattedTime = formatTime(newSeconds);
      document.title = `${prefix}${formattedTime}${suffix}`;
      
      // 重新开始计时
      startTimer();
    } else {
      // 恢复原始标题
      document.title = originalTitleRef.current;
    }
  };
  
  // 启动计时器的函数
  const startTimer = () => {
    // 设置定时器每秒更新一次标题
    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const remainingSeconds = Math.max(0, Math.ceil((endTimeRef.current - now) / 1000));
      
      if (remainingSeconds <= 0) {
        // 倒计时结束，恢复原始标题并清除定时器
        document.title = originalTitleRef.current;
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      } else {
        // 更新标题显示剩余时间
        const formattedTime = formatTime(remainingSeconds);
        document.title = `${prefix}${formattedTime}${suffix}`;
      }
    }, 1000);
  };
  
  useEffect(() => {
    // 如果不是激活状态，恢复原始标题并清除定时器
    if (!isActive) {
      document.title = originalTitleRef.current;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    
    // 如果秒数有效，开始倒计时
    if (seconds > 0) {
      // 保存原始标题（仅在首次运行时）
      if (!intervalRef.current) {
        originalTitleRef.current = document.title || originalTitle;
      }
      
      // 清除现有定时器
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      // 设置结束时间
      endTimeRef.current = Date.now() + seconds * 1000;
      
      // 立即更新标题一次
      const remainingSeconds = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000));
      const formattedTime = formatTime(remainingSeconds);
      document.title = `${prefix}${formattedTime}${suffix}`;
      
      // 启动计时器
      startTimer();
    } else {
      // 秒数无效，恢复原始标题
      document.title = originalTitleRef.current;
    }
    
    // 清理函数
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // 恢复原始标题
      document.title = originalTitleRef.current;
    };
  }, [seconds, prefix, suffix, isActive, originalTitle]);
  
  // 格式化时间为 MM:SS 或 HH:MM:SS 格式
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  };
  
  // 返回重置函数，允许从外部重置计时器
  return { resetTimer };
};

export default useTabCountdown; 